import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/admin/lib/api'
import type {
  PreviewResponse,
  TemplateDetail,
  TemplateListRow,
  TemplateKind,
  VariableCatalogEntry,
} from './types'

const detailKey = (id: number | string) => ['email-templates', 'detail', String(id)]

export function useEmailTemplateList(
  kind?: TemplateKind | 'all',
  tag?: string,
) {
  return useQuery<TemplateListRow[]>({
    queryKey: ['email-templates', kind ?? 'all', tag ?? 'no-tag'],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (kind && kind !== 'all') params.kind = kind
      if (tag) params.tag = tag
      const res = await api.get('/admin/email-templates/', { params })
      return res.data
    },
  })
}

export function useEmailTemplateTags() {
  return useQuery<string[]>({
    queryKey: ['email-template-tags'],
    queryFn: async () => {
      const res = await api.get('/admin/email-templates/tags/')
      return res.data
    },
  })
}

export function useEmailTemplateVariables() {
  return useQuery<VariableCatalogEntry[]>({
    queryKey: ['email-template-variables'],
    queryFn: async () => {
      const res = await api.get('/admin/email-templates/variables/')
      return res.data
    },
  })
}

export function useEmailTemplate(id: number | string | undefined) {
  return useQuery<TemplateDetail>({
    queryKey: detailKey(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get(`/admin/email-templates/${id}/`)
      return res.data
    },
  })
}

export function useCreateTemplate() {
  const qc = useQueryClient()
  return useMutation<TemplateDetail, unknown, Partial<TemplateDetail>>({
    mutationFn: async (payload) => {
      const res = await api.post('/admin/email-templates/', payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })
}

export function useUpdateTemplate(id: number | string) {
  const qc = useQueryClient()
  return useMutation<TemplateDetail, unknown, Partial<TemplateDetail>>({
    mutationFn: async (payload) => {
      const res = await api.put(`/admin/email-templates/${id}/`, payload)
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['email-templates'] })
      qc.setQueryData(detailKey(id), data)
    },
  })
}

export function useDuplicateTemplate() {
  const qc = useQueryClient()
  return useMutation<TemplateDetail, unknown, number>({
    mutationFn: async (id) => {
      const res = await api.post(`/admin/email-templates/${id}/duplicate/`)
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['email-templates'] })
      qc.setQueryData(detailKey(data.id), data)
    },
  })
}

export interface OverridePayload {
  name?: string
  subject: string
  body_html: string
  body_text?: string
}

export function useSaveTemplateOverride(id: number | string) {
  const qc = useQueryClient()
  return useMutation<unknown, unknown, OverridePayload>({
    mutationFn: async (payload) => {
      const res = await api.put(`/admin/email-templates/${id}/override/`, payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) })
    },
  })
}

export function useDeleteTemplateOverride(id: number | string) {
  const qc = useQueryClient()
  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      await api.delete(`/admin/email-templates/${id}/override/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) })
    },
  })
}

export function useDeleteTemplate() {
  const qc = useQueryClient()
  return useMutation<void, unknown, { id: number }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/admin/email-templates/${id}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })
}

export function usePreviewTemplate(id: number | string) {
  return useMutation<
    PreviewResponse,
    unknown,
    { context?: Record<string, unknown>; branding_option?: 'GENERIC' | 'COBRANDED' | 'BRANDED' }
  >({
    mutationFn: async (payload) => {
      const res = await api.post(`/admin/email-templates/${id}/preview/`, payload)
      return res.data
    },
  })
}

export function useTestSendTemplate(id: number | string) {
  return useMutation<
    {
      sent_to: string
      subject: string
      attachment_count: number
      attachments_unavailable?: number
    },
    unknown,
    {
      recipient_email: string
      context?: Record<string, unknown>
      branding_option?: 'GENERIC' | 'COBRANDED' | 'BRANDED'
    }
  >({
    mutationFn: async (payload) => {
      const res = await api.post(`/admin/email-templates/${id}/test-send/`, payload)
      return res.data
    },
  })
}
